import { useState } from 'react';
import { REGION_OPTIONS, YEAR_OPTIONS, SORT_OPTIONS } from '@/types/video';

interface FilterBarProps {
  region: string;
  year: string;
  sort: 'update' | 'hot';
  resultCount: number;
  onRegion: (v: string) => void;
  onYear: (v: string) => void;
  onSort: (v: 'update' | 'hot') => void;
}

export default function FilterBar({
  region,
  year,
  sort,
  resultCount,
  onRegion,
  onYear,
  onSort,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const hasFilter = region !== '全部' || year !== '全部' || sort !== 'update';

  const reset = () => {
    onRegion('全部');
    onYear('全部');
    onSort('update');
  };

  const Chip = ({
    active,
    label,
    onClick,
  }: {
    active: boolean;
    label: string;
    onClick: () => void;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
        active
          ? 'bg-primary-500 text-background-50'
          : 'bg-background-800 text-foreground-300 hover:bg-background-700 hover:text-foreground-50'
      }`}
    >
      {label}
    </button>
  );

  const FilterGroup = ({
    label,
    children,
  }: {
    label: string;
    children: React.ReactNode;
  }) => (
    <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
      <span className="w-12 flex-shrink-0 text-sm text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );

  return (
    <div className="rounded-lg border border-background-800/80 bg-background-900/60 p-4 md:p-5">
      <div className="flex items-center justify-between gap-3 md:hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-3 py-2 text-sm text-foreground-100"
        >
          <i className="ri-filter-3-line" />
          筛选条件
          <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-base`} />
        </button>
        <span className="text-xs text-foreground-400">共 {resultCount} 部</span>
      </div>

      <div className={`${open ? 'mt-4 flex' : 'hidden'} flex-col gap-4 md:mt-0 md:flex`}>
        <FilterGroup label="地区">
          {REGION_OPTIONS.map((r) => (
            <Chip key={r} active={region === r} label={r} onClick={() => onRegion(r)} />
          ))}
        </FilterGroup>
        <FilterGroup label="年份">
          {YEAR_OPTIONS.map((y) => (
            <Chip key={y} active={year === y} label={y} onClick={() => onYear(y)} />
          ))}
        </FilterGroup>
        <FilterGroup label="排序">
          {SORT_OPTIONS.map((s) => (
            <Chip key={s.key} active={sort === s.key} label={s.label} onClick={() => onSort(s.key)} />
          ))}
        </FilterGroup>
        <div className="flex items-center justify-between border-t border-background-800 pt-3">
          <span className="hidden text-xs text-foreground-400 md:block">共 {resultCount} 部视频</span>
          {hasFilter && (
            <button
              type="button"
              onClick={reset}
              className="flex items-center gap-1 whitespace-nowrap text-xs text-primary-400 transition-colors hover:text-primary-300"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          )}
        </div>
      </div>
    </div>
  );
}